import React, { Suspense, useMemo } from 'react';
import { useLocation, Navigate } from 'react-router-dom';
import { getCanonicalLandingPath, normalizeLandingPath } from './landingRoutePolicy';
import { shouldUseRedesignForSurface, useUiReleaseMode } from '../config/uiRelease';
import BespokeLandingReleaseShell, { isBespokeLandingPath } from '../components/landing/BespokeLandingReleaseShell';

// PageLoader spinner — same as the one in App.tsx
const PageLoader: React.FC = () => (
    <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#0a0a0a',
    }}>
        <div style={{
            width: '40px',
            height: '40px',
            border: '3px solid rgba(255, 77, 0, 0.2)',
            borderTopColor: '#ff4d00',
            borderRadius: '50%',
            animation: 'spin 0.8s linear infinite',
        }} />
    </div>
);

// Cache for already-resolved lazy components to avoid re-creating them on every render
const componentCache = new Map<string, React.LazyExoticComponent<React.ComponentType<any>>>();

/**
 * Dynamic landing page loader.
 * 
 * Instead of 1,055 explicit <Route> definitions in App.tsx (which bloats
 * the main bundle to 387KB), this component dynamically resolves the
 * correct landing page component from a lazily-loaded route map.
 *
 * The route map itself (~114KB source) loads in its own chunk, only
 * when a landing page is actually visited. Each individual landing page
 * component is ALSO lazy-loaded from there.
 *
 * Net effect: App.tsx (index.js) goes from 387KB → ~50KB.
 */
const LandingPageLoader: React.FC = () => {
    const location = useLocation();
    const releaseMode = useUiReleaseMode();
    const pathname = normalizeLandingPath(location.pathname);
    const canonicalPath = getCanonicalLandingPath(pathname);
    const useBespokeRedesign = shouldUseRedesignForSurface(releaseMode, 'landing-bespoke')
        && isBespokeLandingPath(pathname);
    const cacheKey = `${pathname}:${useBespokeRedesign ? 'redesign' : 'legacy'}`;

    const LazyComponent = useMemo(() => {
        // Check cache first
        if (componentCache.has(cacheKey)) {
            return componentCache.get(cacheKey)!;
        }

        // Create a lazy component that dynamically imports the route map
        // and resolves the correct component
        const component = React.lazy(async () => {
            const { landingRouteMap } = await import('./landingRouteMap');
            const factory = landingRouteMap[pathname];
            
            if (!factory) {
                // Return a redirect component for unknown paths
                return {
                    default: () => <Navigate to="/" replace />
                };
            }

            // Get the lazy component from the factory
            const LazyInner = factory();
            
            // We need to resolve the inner lazy component and return its module
            // Since factory() returns React.lazy(), we need to unwrap it
            // The trick: return a wrapper that renders the inner lazy component
            return {
                default: (props: any) => useBespokeRedesign ? (
                    <BespokeLandingReleaseShell>
                        <Suspense fallback={<PageLoader />}>
                            <LazyInner {...props} />
                        </Suspense>
                    </BespokeLandingReleaseShell>
                ) : (
                    <Suspense fallback={<PageLoader />}>
                        <LazyInner {...props} />
                    </Suspense>
                )
            };
        });

        // Cache it for future navigations
        componentCache.set(cacheKey, component);
        return component;
    }, [cacheKey, pathname, useBespokeRedesign]);

    if (canonicalPath) {
        return (
            <Navigate
                to={{
                    pathname: canonicalPath,
                    search: location.search,
                    hash: location.hash,
                }}
                replace
            />
        );
    }

    return (
        <Suspense fallback={<PageLoader />}>
            <LazyComponent />
        </Suspense>
    );
};

export default LandingPageLoader;
